import React, { useState, useEffect } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import './LogModals.css';

interface Animal {
  id: number;
  name: string;
  category: string;
  weight_lbs: number | null;
}

interface HarvestLogModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const HarvestLogModal: React.FC<HarvestLogModalProps> = ({ onClose, onSuccess }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [selectedAnimal, setSelectedAnimal] = useState<number | null>(null);
  const [saleWeight, setSaleWeight] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ points: number; message: string } | null>(null);

  useEffect(() => {
    loadAnimals();
  }, []);

  const loadAnimals = async () => {
    try {
      const data = await bridgeClubAPI.getAnimals();
      // Filter to meat animals (pigs, goats, sheep, cattle, poultry)
      const meatAnimals = data.filter((a: Animal) =>
        ['swine', 'goat', 'sheep', 'cattle', 'poultry', 'rabbit'].includes(a.category)
      );
      setAnimals(meatAnimals);
    } catch (err) {
      setError('Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnimal) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await bridgeClubAPI.logHarvestForPoints({
        animal_id: selectedAnimal,
        sale_weight: saleWeight ? parseFloat(saleWeight) : undefined,
        sale_price: salePrice ? parseFloat(salePrice) : undefined,
      });

      if (response.success) {
        setSuccess({
          points: response.points_earned,
          message: response.message,
        });
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to log harvest');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to log harvest');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'swine': return '🐷';
      case 'goat': return '🐐';
      case 'sheep': return '🐑';
      case 'cattle': return '🐄';
      case 'poultry': return '🐔';
      case 'rabbit': return '🐰';
      default: return '🐾';
    }
  };

  return (
    <div className="log-modal-overlay" onClick={onClose}>
      <div className="log-modal" onClick={e => e.stopPropagation()}>
        <div className="log-modal-header">
          <span className="log-modal-icon">🔪</span>
          <h2>Log Harvest</h2>
          <button className="log-modal-close" onClick={onClose}>&times;</button>
        </div>

        {success ? (
          <div className="log-success">
            <span className="success-icon">✅</span>
            <p className="success-points">+{success.points} points!</p>
            <p className="success-message">{success.message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {loading ? (
              <div className="log-loading">Loading...</div>
            ) : animals.length === 0 ? (
              <div className="log-empty">
                <p>No animals to harvest.</p>
                <p className="log-empty-hint">
                  Add livestock or poultry to log harvests.
                </p>
              </div>
            ) : (
              <>
                <div className="harvest-warning">
                  <span>⚠️</span>
                  <p>This will mark the animal as harvested. Each animal can only be harvested once per year.</p>
                </div>

                <div className="form-group">
                  <label>Select Animal</label>
                  <div className="animal-select-grid">
                    {animals.map(animal => (
                      <button
                        key={animal.id}
                        type="button"
                        className={`animal-select-btn ${selectedAnimal === animal.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAnimal(animal.id)}
                      >
                        <span className="animal-select-icon">{getCategoryIcon(animal.category)}</span>
                        <span className="animal-select-name">{animal.name || `${animal.category} #${animal.id}`}</span>
                        {animal.weight_lbs && (
                          <span className="animal-select-weight">{animal.weight_lbs} lbs</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="saleWeight">Sale Weight (lbs) - Optional</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="saleWeight"
                    value={saleWeight}
                    onChange={e => setSaleWeight(e.target.value)}
                    onBlur={e => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val > 0) setSaleWeight(String(val));
                      else setSaleWeight('');
                    }}
                    placeholder="Weight at sale"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="salePrice">Sale Price ($) - Optional</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="salePrice"
                    value={salePrice}
                    onChange={e => setSalePrice(e.target.value)}
                    onBlur={e => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val > 0) setSalePrice(String(val));
                      else setSalePrice('');
                    }}
                    placeholder="What did you sell it for?"
                  />
                </div>

                {error && <div className="log-error">{error}</div>}

                <div className="log-modal-actions">
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit btn-harvest"
                    disabled={!selectedAnimal || submitting}
                  >
                    {submitting ? 'Logging...' : 'Log Harvest'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default HarvestLogModal;
